import { QueueTypeS } from "@ugg/shared/utils/queue-type-helpers";
import colors from "@ugitgud/legos/plugins/colors.json";

export const ALL_TIERS = ["S+", "S", "A", "B", "C", "D"] as const;

export const TIER_CHANGE_UP_COLOR = "#18F8EB";
export const TIER_CHANGE_DOWN_COLOR = "#FF4655";
export type TierGrade = (typeof ALL_TIERS)[number];

export const TIER_GRADE_STYLES = {
  "S+": {
    color: colors["accent-orange"]["700"],
    badgeBg: colors["tier-badge-bg"]["600"],
    cardClass: "border-t-accent-orange-700",
    badgeClass: "border-accent-orange-700 text-accent-orange-700 bg-tier-badge-bg-600",
  },
  S: {
    color: colors["accent-blue"]["400"],
    badgeBg: colors["tier-badge-bg"]["500"],
    cardClass: "border-t-accent-blue-400",
    badgeClass: "border-accent-blue-400 text-accent-blue-400 bg-tier-badge-bg-500",
  },
  A: {
    color: colors["accent-blue"]["200"],
    badgeBg: colors["tier-badge-bg"]["400"],
    cardClass: "border-t-accent-blue-200",
    badgeClass: "border-accent-blue-200 text-accent-blue-200 bg-tier-badge-bg-400",
  },
  B: {
    color: colors["white"],
    badgeBg: colors["tier-badge-bg"]["300"],
    cardClass: "border-t-white",
    badgeClass: "border-white text-white bg-tier-badge-bg-300",
  },
  C: {
    color: colors["accent-red"]["100"],
    badgeBg: colors["tier-badge-bg"]["200"],
    cardClass: "border-t-accent-red-100",
    badgeClass: "border-accent-red-100 text-accent-red-100 bg-tier-badge-bg-200",
  },
  D: {
    color: colors["accent-red"]["450"],
    badgeBg: colors["tier-badge-bg"]["100"],
    cardClass: "border-t-accent-red-450",
    badgeClass: "border-accent-red-450 text-accent-red-450 bg-tier-badge-bg-100",
  },
} satisfies Record<TierGrade, { color: string; badgeBg: string; cardClass: string; badgeClass: string }>;

export function getTierGradeColorHex(grade: string) {
  return (TIER_GRADE_STYLES[grade as TierGrade] ?? TIER_GRADE_STYLES.B).color;
}

export function getTierGradeBadgeBgHex(grade: string) {
  return (TIER_GRADE_STYLES[grade as TierGrade] ?? TIER_GRADE_STYLES.B).badgeBg;
}

export function getTierListPickRateCutoff(queueType?: string) {
  if (queueType === QueueTypeS.ONE_FOR_ALL) {
    return 0;
  }
  return 0.5;
}

export function getTier(winrate: number) {
  if (winrate < 45) {
    return "D";
  } else if (winrate < 48.5) {
    return "C";
  } else if (winrate < 51.5) {
    return "B";
  } else if (winrate < 53) {
    return "A";
  } else if (winrate < 55) {
    return "S";
  } else {
    return "S+";
  }
}

export function getTierColor(winrate: number) {
  if (isNaN(winrate)) {
    return "";
  }

  if (winrate < 45) {
    return "shinggo-tier";
  } else if (winrate < 48.5) {
    return "meh-tier";
  } else if (winrate < 51.5) {
    return "okay-tier";
  } else if (winrate < 53) {
    return "good-tier";
  } else if (winrate < 55) {
    return "great-tier";
  } else {
    return "volxd-tier";
  }
}

export function getTierColorHex(winrate: number, defaultColorHex = colors["white"]) {
  if (isNaN(winrate)) {
    return "";
  }

  if (winrate < 45) {
    return colors["accent-red"]["450"];
  } else if (winrate < 48.5) {
    return colors["accent-red"]["100"];
  } else if (winrate < 51.5) {
    return defaultColorHex;
  } else if (winrate < 53) {
    return colors["accent-blue"]["200"];
  } else if (winrate < 55) {
    return colors["accent-blue"]["400"];
  } else {
    return colors["accent-orange"]["700"];
  }
}

export function getLuTier(stdevs: number) {
  if (stdevs >= 2) {
    return "S+";
  } else if (stdevs >= 0.75) {
    return "S";
  } else if (stdevs >= 0) {
    return "A";
  } else if (stdevs >= -0.5) {
    return "B";
  } else if (stdevs >= -0.75) {
    return "C";
  } else {
    return "D";
  }
}

export function getLuTierColor(stdevs: number) {
  if (stdevs >= 2) {
    return "volxd-tier";
  } else if (stdevs >= 0.75) {
    return "great-tier";
  } else if (stdevs >= 0) {
    return "good-tier";
  } else if (stdevs >= -0.5) {
    return "okay-tier";
  } else if (stdevs >= -0.75) {
    return "meh-tier";
  } else {
    return "shinggo-tier";
  }
}

export function getLuTierColorHex(stdevs: number) {
  if (stdevs >= 2) {
    return colors["accent-orange"]["700"];
  } else if (stdevs >= 0.75) {
    return colors["accent-blue"]["400"];
  } else if (stdevs >= 0) {
    return colors["accent-blue"]["200"];
  } else if (stdevs >= -0.5) {
    return colors["white"];
  } else if (stdevs >= -0.75) {
    return colors["accent-red"]["100"];
  } else {
    return colors["accent-red"]["450"];
  }
}
